import axios from "axios";
import Cookies from "js-cookie";
import { createContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";

export const JobContext = createContext();

export const JobProvider = ({ children }) => {
  const navigate = useNavigate();
  const token = Cookies.get("token");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modal, setModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  const [form, setForm] = useState({
    title: "",
    job_description: "",
    job_qualification: "",
    job_type: "",
    job_tenure: "",
    job_status: 1,
    company_name: "",
    company_image_url: "",
    company_city: "",
    salary_min: "",
    salary_max: "",
  });

  const [currentID, setCurrentID] = useState(null);

  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [company, setCompany] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const normalizeCity = (c = "") =>
    c
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const handleStatus = (status) =>
    status === 1
      ? { label: "Dibuka", className: "bg-green-100 text-green-700" }
      : { label: "Ditutup", className: "bg-red-100 text-red-700" };

  const formatRupiah = (number) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(number);

  const salaryOptions = [
    { label: "Rp1 - Rp1.000.000", min: 1, max: 1000000 },
    { label: "Rp1.000.000 - Rp5.000.000", min: 1000000, max: 5000000 },
    { label: "Rp5.000.000 - Rp10.000.000", min: 5000000, max: 10000000 },
    { label: "Rp10.000.000 - Rp20.000.000", min: 10000000, max: 20000000 },
    { label: "Rp20.000.000 - Rp50.000.000", min: 20000000, max: 50000000 },
    { label: "Rp50.000.000 - Rp100.000.000", min: 50000000, max: 100000000 },
  ];

  const statuses = [
    { label: "Dibuka", value: 1 },
    { label: "Ditutup", value: 0 },
  ];

  const openSuccess = (msg) =>
    setModal({
      isOpen: true,
      type: "success",
      title: "Berhasil",
      message: msg,
      onConfirm: null,
    });

  const openError = (msg) =>
    setModal({
      isOpen: true,
      type: "error",
      title: "Gagal",
      message: msg,
      onConfirm: null,
    });

  const openConfirm = (msg, onConfirm) =>
    setModal({
      isOpen: true,
      type: "confirm",
      title: "Konfirmasi",
      message: msg,
      onConfirm,
    });

  const closeModal = () => setModal((prev) => ({ ...prev, isOpen: false }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmptyField =
      !form.title.trim() ||
      !form.job_description.trim() ||
      !form.job_qualification.trim() ||
      !form.job_type.trim() ||
      !form.job_tenure.trim() ||
      !form.company_name.trim() ||
      !form.company_image_url.trim() ||
      !form.company_city.trim() ||
      form.salary_min === "" ||
      form.salary_max === "";

    if (isEmptyField) return openError("Semua field wajib diisi!");

    try {
      setLoading(true);
      if (!token) return openError("Token tidak ditemukan, login ulang!");

      const payload = {
        title: form.title,
        job_description: form.job_description,
        job_qualification: form.job_qualification,
        job_type: form.job_type,
        job_tenure: form.job_tenure,
        job_status: Number(form.job_status),
        company_name: form.company_name,
        company_image_url: form.company_image_url,
        company_city: form.company_city,
        salary_min: Number(form.salary_min),
        salary_max: Number(form.salary_max),
      };

      if (currentID) {
        await axios.put(
          `https://final-project-api-alpha.vercel.app/api/jobs/${currentID}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        openSuccess("Data lowongan berhasil diubah!");
      } else {
        await axios.post(
          "https://final-project-api-alpha.vercel.app/api/jobs",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        openSuccess("Data lowongan berhasil disimpan!");
      }

      setForm({
        title: "",
        job_description: "",
        job_qualification: "",
        job_type: "",
        job_tenure: "",
        job_status: 1,
        company_name: "",
        company_image_url: "",
        company_city: "",
        salary_min: "",
        salary_max: "",
      });
      setCurrentID(null);
      fetchJobs();
      navigate("/dashboard/list-job-vacancy");
    } catch (err) {
      console.log(err);
      openError(err.response?.data?.message || "Gagal menyimpan data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setCurrentID(job._id);
    setForm({ ...job });
    navigate(`/dashboard/list-job-vacancy/form/${job._id}`);
  };

  const handleDelete = (id) => {
    openConfirm("Apakah Anda yakin ingin menghapus data ini?", async () => {
      try {
        await axios.delete(
          `https://final-project-api-alpha.vercel.app/api/jobs/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchJobs();
        closeModal();
        openSuccess("Data berhasil dihapus!");
      } catch (err) {
        console.log(err);
        openError("Gagal menghapus data");
      }
    });
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://final-project-api-alpha.vercel.app/api/jobs"
      );
      setData(res.data);
      setError(null);
    } catch (err) {
      console.log(err);
      setError("Gagal memuat data lowongan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const filteredJobs = useMemo(() => {
    return data.filter((job) => {
      const matchSearch = job.title
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchCity = city ? normalizeCity(job.company_city) === city : true;
      const matchCompany = company ? job.company_name === company : true;
      const matchType = filterType ? job.job_type === filterType : true;
      const matchStatus = filterStatus
        ? job.job_status === Number(filterStatus)
        : true;

      return (
        matchSearch && matchCity && matchCompany && matchType && matchStatus
      );
    });
  }, [data, search, city, company, filterType, filterStatus]);

  const cities = useMemo(
    () => [...new Set(data.map((j) => normalizeCity(j.company_city)))],
    [data]
  );
  const companies = useMemo(
    () => [...new Set(data.map((j) => j.company_name))],
    [data]
  );
  const jobTypes = useMemo(
    () => [...new Set(data.map((j) => j.job_type))],
    [data]
  );

  const states = {
    data,
    loading,
    error,
    form,
    currentID,
    search,
    city,
    company,
    filterType,
    filterStatus,
    filteredJobs,
    cities,
    companies,
    jobTypes,
    statuses,
    salaryOptions,
  };

  const actions = {
    setForm,
    setCurrentID,
    setSearch,
    setCity,
    setCompany,
    setFilterType,
    setFilterStatus,
    handleChange,
    handleSubmit,
    handleEdit,
    handleDelete,
    fetchJobs,
    normalizeCity,
    formatRupiah,
    handleStatus,
    modal,
    setModal,
    openSuccess,
    openError,
    openConfirm,
    closeModal,
  };

  return (
    <JobContext.Provider value={{ states, actions }}>
      {children}
      {modal.isOpen && (
        <Modal
          isOpen={modal.isOpen}
          onClose={closeModal}
          type={modal.type}
          title={modal.title}
          message={modal.message}
          onConfirm={modal.onConfirm}
        />
      )}
    </JobContext.Provider>
  );
};
